"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FolderTree, Wrench, FileText, Compass, Code2, LineChart, AlertTriangle, ArrowRight, Layers, Sparkles, Search, SlidersHorizontal, RefreshCw, Maximize2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const activities = [{
  id: 1,
  title: "Catalogue and search demand alignment",
  description: "Aligning your product catalogue and category structure with real search demand so that when customers search for items you sell, your store appears.",
  icon: FolderTree
}, {
  id: 2,
  title: "Technical ecommerce auditing and fixes",
  description: "Auditing and improving technical SEO so search engines crawl, index and prioritise key products and collections without wasting resources on duplicate URLs.",
  icon: Wrench
}, {
  id: 3,
  title: "Product and category page optimisation",
  description: "Optimising product and category pages across copy, metadata, imagery, and internal links to improve both search rankings and buyer confidence.",
  icon: FileText
}, {
  id: 4,
  title: "Ecommerce content strategy",
  description: "Developing an ecommerce content strategy that assists product discovery through structured buying guides, customer FAQs and comparison content.",
  icon: Compass
}, {
  id: 5,
  title: "Implementation-ready developer briefs",
  description: "Providing implementation-ready recommendations for your developers, or making changes directly within platforms like Shopify or WooCommerce when access is provided.",
  icon: Code2
}, {
  id: 6,
  title: "Performance tracking and strategy refinement",
  description: "Monitoring performance and refining strategy based on Google Search Console data, ecommerce analytics and organic revenue trends.",
  icon: LineChart
}];
const differenceFactors = [{
  id: 1,
  title: "Catalogue scale",
  description: "Standard websites manage dozens of pages. Online stores manage hundreds or thousands of products and category hubs that all compete for search visibility.",
  icon: Maximize2
}, {
  id: 2,
  title: "Product variants",
  description: "Colours, sizes, materials and pack configurations multiply URLs quickly, requiring clear indexation and canonical rules to prevent duplicate content.",
  icon: Layers
}, {
  id: 3,
  title: "Faceted navigation",
  description: "Customer filter combinations can generate endless thin URL variations that consume crawl budget if not carefully managed.",
  icon: SlidersHorizontal
}, {
  id: 4,
  title: "Dynamic stock levels",
  description: "Inventory changes daily. Out-of-stock and discontinued items require strategic redirect or holding patterns to protect accumulated ranking equity.",
  icon: RefreshCw
}];
const auditProblems = [{
  id: 1,
  title: "Products missing for obvious searches",
  description: "Product pages failing to appear for specific queries because they lack targeted keywords, structured data or supportive internal linking.",
  example: 'Illustrative search: "men\'s waterproof hiking boots UK size 10"'
}, {
  id: 2,
  title: "Category pages stranded on pages two or three",
  description: "High-intent commercial terms lacking unique category copy or targeting the wrong search phrases to rank competitively.",
  example: 'Illustrative searches: "blackout curtains", "garden furniture sale"'
}, {
  id: 3,
  title: "Duplicate or manufacturer product descriptions",
  description: "Supplier descriptions copied verbatim across multiple product pages, creating thin content issues and weakening ranking potential."
}, {
  id: 4,
  title: "Messy site architecture and faceted navigation",
  description: "Filter combinations generating thousands of near-identical URLs, diluting link equity and wasting search engine crawl resources."
}, {
  id: 5,
  title: "Slow or confusing mobile user experience",
  description: "Friction in mobile navigation, sluggish load times or awkward layouts causing shoppers to abandon before reaching a product."
}, {
  id: 6,
  title: "Overreliance on paid advertising channels",
  description: "Rising cost per click and unstable return on ad spend on search and social channels, without a compounding organic asset."
}, {
  id: 7,
  title: "Traffic that fails to translate into revenue",
  description: "Healthy visitor numbers in analytics that do not convert into baskets, checkouts or orders because the queries lack purchase intent."
}];
export default function EcommerceOverview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  return <section id="ecommerce-overview" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Subsection 1: What an ecommerce SEO consultant does */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Consultant Role & Value
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            What an ecommerce SEO consultant does
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ecommerce SEO operates at an entirely different scale to standard brochure websites. I optimise online stores to increase organic visibility for searches that lead directly to purchases, combining technical, content and commercial thinking across your entire catalogue.
          </p>
        </div>

        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activities.map((activity, i) => {
          const Icon = activity.icon;
          return <motion.div key={activity.id} data-index={i} variants={fadeIn} className="flex">
                <Card className="flex w-full flex-col justify-between border-border bg-card shadow-xs hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {activity.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {activity.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Subsection 2: Why ecommerce SEO differs from general SEO */}
        <div className="mt-20 pt-16 border-t border-border">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-default text-foreground">
              Why ecommerce SEO differs from general SEO
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              A general SEO campaign often focuses on a handful of static service pages. Online stores handle dynamic inventories, complex taxonomy, and multi-faceted product variations where technical decisions have immediate commercial consequences.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differenceFactors.map((factor, i) => {
            const Icon = factor.icon;
            return <div key={factor.id} data-index={i} className="rounded-xl border border-border bg-card p-6 shadow-xs">
                  <div className="size-9 rounded-lg bg-secondary text-primary flex items-center justify-center mb-4">
                    <Icon className="size-4.5" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">
                    {factor.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {factor.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {/* Subsection 3: Problems I see when auditing online stores */}
        <div className="mt-20 pt-16 border-t border-border">
          <div className="max-w-3xl mb-12">
            <Badge variant="outline" className="mb-3 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              Common Online Store Roadblocks
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-default text-foreground">
              Problems I see when auditing online stores
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Many stores rely heavily on paid channels where costs per click rise continuously. When I conduct an{" "}
              <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
                ecommerce SEO audit
              </Link>
              , I repeatedly uncover structural and intent gaps that suppress organic visibility and prevent commercial growth:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {auditProblems.map((problem, i) => <div key={problem.id} data-index={i} className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xs flex gap-4 items-start">
                <div className="size-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle className="size-4" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <h3 className="text-base font-semibold text-foreground">
                    {problem.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {problem.description}
                  </p>
                  {problem.example && <div className="mt-2 pt-2 border-t border-border/60">
                      <p className="text-xs font-mono text-muted-foreground flex items-center gap-1.5">
                        <Search className="size-3 text-primary shrink-0" />
                        <span>{problem.example}</span>
                      </p>
                      <span className="text-[10px] text-muted-foreground/80 block mt-0.5">
                        Illustrative examples, not client data
                      </span>
                    </div>}
                </div>
              </div>)}
          </div>

          {/* Contextual Link to SEO Audits */}
          <div className="mt-12 rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Identify where your store is losing organic search revenue
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                A thorough audit uncovers crawl leaks, catalogue gaps, and high-converting keyword opportunities.
              </p>
            </div>
            <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 shrink-0">
              Learn more about my SEO audits
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}